import deepmerge from "deepmerge";

/**
 * Work title that gets assigned to PR's whenever they have no title specified
 */
const DEFAULT_WORK_TITLE = "PR Professional";

/**
 * Applies "override" keys in the enriched data by merging them into the main
 * structure.
 *
 * @param {Array<Object>} data - The enriched data with potential "override" keys.
 * @returns {Array<Object>} The data with "override" keys merged and removed.
 */
export function applyOverrides(data) {
  return data.map((item) => {
    if (item.override && typeof item.override === "object") {
      const mergedItem = deepmerge(item, item.override);
      delete mergedItem.override;

      return mergedItem;
    }
    return item;
  });
}

/**
 * Fetches detailed profile data based on a list of profile IDs.
 *
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.apiDataUrl - The base URL for fetching profile data.
 * @param {Object} params.extraHeaders - Additional headers to include in the request (e.g. auth).
 * @param {Array<string>} params.profileIds - A list of profile IDs to fetch details for.
 * @returns {Promise<Object>} A promise that resolves to a map of profile details keyed by profile ID.
 */
async function fetchProfilesDetails({ apiDataUrl, extraHeaders, profileIds }) {
  try {
    // Construct the query string to filter profiles by the provided IDs
    const query = profileIds.map((id) => id).join(",");
    const endpoint = `${apiDataUrl}?profile_id=in.(${query})`;

    const response = await fetch(endpoint, {
      headers: extraHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch profiles: ${response.statusText}`);
    }

    const profiles = await response.json();

    // Transform the array of profiles into a map, keyed by profile_id
    const profilesMap = profiles.reduce((accumulator, profile) => {
      return { ...accumulator, [profile.profile_id]: profile };
    }, {});

    return profilesMap;
  } catch (error) {
    throw new Error(`Error fetching profiles details: ${error.message}`);
  }
}

/**
 * Fetches profile emails based on a list of profile IDs.
 *
 * @param {Object} params - The parameters for the API request.
 * @param {string} params.apiUserProfilesUrl - The base URL for user profiles.
 * @param {Object} params.extraHeaders - Additional headers to include in the request (e.g. auth).
 * @param {Array<string>} params.profileIds - A list of profile IDs to fetch emails for.
 * @returns {Promise<Object>} A promise that resolves to a map of profile emails keyed by profile ID.
 */
async function fetchProfilesEmails({ apiUserProfilesUrl, extraHeaders, profileIds }) {
  try {
    // Construct the query string to filter profiles by the provided IDs
    const query = profileIds.map((id) => id).join(",");
    const endpoint = `${apiUserProfilesUrl}?profile_id=in.(${query})&select=profile_id,...users(email)`;

    const response = await fetch(endpoint, {
      headers: extraHeaders,
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch user profiles emails: ${response.statusText}`);
    }

    const profiles = await response.json();

    // Transform the array of profiles into a map, keyed by profile_id
    const profilesMap = profiles.reduce((accumulator, profile) => {
      return { ...accumulator, [profile.profile_id]: profile.email };
    }, {});

    return profilesMap;
  } catch (error) {
    throw new Error(`Error fetching user profiles emails: ${error.message}`);
  }
}

/**
 * Enriches a list of data with detailed profile information and applies
 * overrides.
 *
 * @param {Object} params - Parameters for fetching and enriching the profiles.
 * @param {Array<Object>} params.data - The list of items containing profile IDs.
 * @param {string} params.apiDataUrl - The base URL for fetching profile data.
 * @param {Object} params.extraHeaders - Additional headers to include in the request (e.g. auth).
 * @returns {Promise<Array<Object>>} The enriched data with detailed profiles added.
 */
export async function enrichDataWithProfiles({ apiDataUrl, apiUserProfilesUrl, data, extraHeaders }) {
  // Extract all unique profile IDs from the data
  const profileIds = data.map((item) => item.profile_id);

  // Fetch detailed profile information for the extracted IDs
  const [profileDetails, profileEmails] = await Promise.all([
    fetchProfilesDetails({ apiDataUrl, profileIds, extraHeaders }),
    fetchProfilesEmails({ apiUserProfilesUrl, profileIds, extraHeaders }),
  ]);

  // Populate profile data with the fetched information
  const entriesWithDetails = data.map((item) => {
    const profile = profileDetails[item.profile_id] || {};
    return {
      ...item,
      profile: { ...profile, title: profile.title ?? DEFAULT_WORK_TITLE, email: profileEmails[item.profile_id] },
    };
  });

  // Return entries with overrides applied
  return applyOverrides(entriesWithDetails);
}
